import React, { useMemo } from 'react'
import { ArrowPathIcon, EyeIcon } from '@heroicons/react/24/outline'
import type { Traslado } from '../../../types/traslados'
import { FLEXY_PRODUCT_TABLE_WAREHOUSES } from '../../../utils/productTableBodegasConfig'
import {
  formatTrasladoFecha,
  getTrasladoAlmacenLabel
} from '../../../utils/trasladosMapper'
import DataTable from '../../ui/DataTable'
import {
  formatTrasladoEstado,
  getTrasladoEstadoBadgeClass,
  isTrasladoErrorEstado
} from './trasladoValidation'

interface FlexyTrasladosTableProps {
  traslados: Traslado[]
  isLoading: boolean
  onView: (traslado: Traslado) => void
  onRetry?: (traslado: Traslado) => void
  retryingId?: number | null
  canManage?: boolean
}

const FlexyTrasladosTable: React.FC<FlexyTrasladosTableProps> = ({
  traslados,
  isLoading,
  onView,
  onRetry,
  retryingId = null,
  canManage = false
}) => {
  const columns = useMemo(
    () => [
      {
        header: 'Consecutivo',
        accessor: 'consecutivo' as keyof Traslado,
        width: '9%',
        cell: (row: Traslado) => (
          <span className="text-sm font-medium text-gray-900" title={row.consecutivo ?? `#${row.id}`}>
            {row.consecutivo ?? `#${row.id}`}
          </span>
        )
      },
      {
        header: 'ID Pedido',
        accessor: 'id_pedido' as keyof Traslado,
        width: '8%',
        cell: (row: Traslado) => (
          <span className="text-sm text-gray-600">
            {row.id_pedido != null && String(row.id_pedido).trim() !== ''
              ? String(row.id_pedido)
              : '—'}
          </span>
        )
      },
      // {
      //   header: 'ID Transf.',
      //   accessor: 'id' as keyof Traslado,
      //   width: '7%',
      //   cell: (row: Traslado) => (
      //     <span className="text-sm text-gray-600">{row.id}</span>
      //   )
      // },
      {
        header: 'ID Order',
        accessor: 'idorder' as keyof Traslado,
        width: '8%',
        cell: (row: Traslado) => (
          <span className="text-sm text-gray-600">
            {row.idorder != null && String(row.idorder).trim() !== ''
              ? String(row.idorder)
              : '—'}
          </span>
        )
      },
      {
        header: 'Estado',
        accessor: 'estado' as keyof Traslado,
        width: '9%',
        cell: (row: Traslado) => (
          <span
            className={`max-w-full truncate rounded-md border px-1.5 py-[0.35rem] text-xs font-medium ${getTrasladoEstadoBadgeClass(row.estado)}`}
            title={formatTrasladoEstado(row.estado)}
          >
            {formatTrasladoEstado(row.estado)}
          </span>
        )
      },
      {
        header: 'Origen',
        accessor: 'idalmacen_origen' as keyof Traslado,
        width: '10%',
        cell: (row: Traslado) => {
          const label = getTrasladoAlmacenLabel(
            row.idalmacen_origen,
            row.nombre_almacen_origen,
            row.codigo_almacen_origen,
            FLEXY_PRODUCT_TABLE_WAREHOUSES
          )
          return (
            <span className="text-sm text-gray-600" title={label}>
              {label}
            </span>
          )
        }
      },
      {
        header: 'Destino',
        accessor: 'idalmacen_destino' as keyof Traslado,
        width: '10%',
        cell: (row: Traslado) => {
          const label = getTrasladoAlmacenLabel(
            row.idalmacen_destino,
            row.nombre_almacen_destino,
            row.codigo_almacen_destino,
            FLEXY_PRODUCT_TABLE_WAREHOUSES
          )
          return (
            <span className="text-sm text-gray-600" title={label}>
              {label}
            </span>
          )
        }
      },
      {
        header: 'Glosa',
        accessor: 'glosa' as keyof Traslado,
        width: '14%',
        maxWidth: '160px',
        cell: (row: Traslado) => (
          <span className="text-sm text-gray-600" title={row.glosa}>
            {row.glosa}
          </span>
        )
      },
      // {
      //   header: 'Lín.',
      //   accessor: 'lineas' as keyof Traslado,
      //   width: '5%',
      //   cell: (row: Traslado) => (
      //     <span className="text-sm text-gray-600">{row.lineas?.length ?? 0}</span>
      //   )
      // },
      {
        header: 'Fecha',
        accessor: 'fecha_creacion' as keyof Traslado,
        width: '13%',
        cell: (row: Traslado) => (
          <span className="block text-xs leading-snug text-gray-500 whitespace-nowrap" title={row.fecha_creacion}>
            {formatTrasladoFecha(row.fecha_creacion)}
          </span>
        )
      },
      {
        header: 'Acción',
        accessor: 'id' as keyof Traslado,
        key: 'acciones',
        width: '12%',
        cellOverflowVisible: true,
        cell: (row: Traslado) => (
          <div className="inline-flex items-center gap-0.5">
            {canManage && isTrasladoErrorEstado(row.estado) && onRetry && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation()
                  onRetry(row)
                }}
                disabled={retryingId === row.id}
                className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-xs text-amber-700 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label={`Reintentar transferencia ${row.id}`}
                title="Reintentar"
              >
                <ArrowPathIcon
                  className={`h-4 w-4 ${retryingId === row.id ? 'animate-spin' : ''}`}
                />
                <span className="hidden xl:inline">Reintentar</span>
              </button>
            )}
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                onView(row)
              }}
              className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-xs text-custom-blue hover:bg-blue-50"
              aria-label={`Ver transferencia ${row.id}`}
              title="Ver"
            >
              <EyeIcon className="h-4 w-4" />
              <span className="hidden xl:inline">Ver</span>
            </button>
          </div>
        )
      }
    ],
    [canManage, onRetry, onView, retryingId]
  )

  return (
    <div className="w-full min-w-0 max-w-full overflow-hidden">
      <DataTable
        data={traslados}
        columns={columns}
        isLoading={isLoading}
        emptyMessage="No hay transferencias para mostrar."
        getRowId={(row) => row.id}
        className="w-full max-w-full"
        compact
      />
    </div>
  )
}

export default FlexyTrasladosTable
